"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const fast_memoize_1 = __importDefault(require("fast-memoize"));
const ethers_1 = require("ethers");
const models_1 = require("./models");
const config_1 = require("./config");
// cache provider
const getProvider = fast_memoize_1.default((network, chain) => {
    const rpcUrls = config_1.config.chains[network][chain].rpcUrls.slice(0, 3); // max of 3 endpoints
    const ethersProviders = [];
    for (let rpcUrl of rpcUrls) {
        const provider = new ethers_1.providers.StaticJsonRpcProvider(rpcUrl);
        ethersProviders.push(provider);
    }
    if (ethersProviders.length === 1) {
        return ethersProviders[0];
    }
    return new ethers_1.providers.FallbackProvider(ethersProviders, 1);
});
const getContractMemo = fast_memoize_1.default((address, abi, cacheKey) => {
    let cached;
    return (provider) => {
        if (!cached) {
            cached = new ethers_1.Contract(address, abi, provider);
        }
        return cached;
    };
});
// cache contract
const getContract = (address, abi, provider) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d, _e, _f, _g;
    let p = provider;
    // memoize function doesn't handle dynamic provider object well, so
    // here we derived a cache key based on connected account address and rpc url.
    const signerAddress = (p === null || p === void 0 ? void 0 : p.getAddress) ? yield (p === null || p === void 0 ? void 0 : p.getAddress()) : '';
    const chainId = (_c = (_b = (_a = p === null || p === void 0 ? void 0 : p.provider) === null || _a === void 0 ? void 0 : _a._network) === null || _b === void 0 ? void 0 : _b.chainId) !== null && _c !== void 0 ? _c : '';
    yield (p === null || p === void 0 ? void 0 : p._networkPromise);
    const fallbackProviderChainId = (_e = (_d = p === null || p === void 0 ? void 0 : p._network) === null || _d === void 0 ? void 0 : _d.chainId) !== null && _e !== void 0 ? _e : '';
    const rpcUrl = (_g = (_f = p === null || p === void 0 ? void 0 : p.connection) === null || _f === void 0 ? void 0 : _f.url) !== null && _g !== void 0 ? _g : '';
    const cacheKey = `${signerAddress}${chainId}${fallbackProviderChainId}${rpcUrl}`;
    return getContractMemo(address, abi, cacheKey)(provider);
});
/**
 * Class with base methods.
 * @namespace Base
 */
class Base {
    /**
     * @desc Instantiates Base class.
     * Returns a new Base class instance.
     * @param {String} network - L1 network name (e.g. 'mainnet', 'kovan', 'goerli')
     * @returns {Object} New Base class instance.
     */
    constructor(network, signer, chainProviders) {
        this.chainProviders = {};
        this.addresses = config_1.config.addresses;
        this.chains = config_1.config.chains;
        this.bonders = config_1.config.bonders;
        this.gasPriceMultiplier = 0;
        /**
         * @desc Returns Ethers provider for specified Chain model.
         * @param {Object} - Chain model.
         * @returns {Object} - Ethers provider.
         */
        this.getChainProvider = (chain) => {
            let chainSlug;
            if (chain instanceof models_1.Chain && (chain === null || chain === void 0 ? void 0 : chain.slug)) {
                chainSlug = chain === null || chain === void 0 ? void 0 : chain.slug;
            }
            else if (typeof chain == 'string') {
                chainSlug = chain;
            }
            else {
                throw new Error(`unknown chain "${chain}"`);
            }
            if (this.chainProviders[chainSlug]) {
                return this.chainProviders[chainSlug];
            }
            return getProvider(this.network, chainSlug);
        };
        this.getContract = getContract;
        if (!this.isValidNetwork(network)) {
            throw new Error(`network is unsupported. Supported networks are: ${this.supportedNetworks.join(',')}`);
        }
        this.network = network;
        if (signer) {
            this.signer = signer;
        }
        if (chainProviders) {
            this.chainProviders = chainProviders;
        }
    }
    setConfigAddresses(addresses) {
        if (addresses.bridges) {
            this.addresses[this.network] = addresses.bridges;
        }
        if (addresses.bonders) {
            this.bonders[this.network] = addresses.bonders;
        }
    }
    setChainProvider(chain, provider) {
        chain = this.toChainModel(chain);
        if (!this.isValidChain(chain.slug)) {
            throw new Error(`unsupported chain "${chain.slug}" for network ${this.network}`);
        }
        this.chainProviders[chain.slug] = provider;
    }
    setChainProviders(chainProviders) {
        for (let chainSlug in chainProviders) {
            const chain = this.toChainModel(chainSlug);
            if (!this.isValidChain(chain.slug)) {
                throw new Error(`unsupported chain "${chain.slug}" for network ${this.network}`);
            }
            this.chainProviders[chain.slug] = chainProviders[chainSlug];
        }
    }
    get supportedNetworks() {
        return Object.keys(this.chains);
    }
    isValidNetwork(network) {
        return this.supportedNetworks.includes(network);
    }
    get supportedChains() {
        return Object.keys(this.chains[this.network]);
    }
    isValidChain(chain) {
        return this.supportedChains.includes(chain);
    }
    /**
     * @desc Returns a Chain model instance with connected provider.
     * @param {Object} - Chain name or model.
     * @returns {Object} - Chain model with connected provider.
     */
    toChainModel(chain) {
        if (typeof chain === 'string') {
            chain = models_1.Chain.fromSlug(chain);
        }
        if (!this.isValidChain(chain.slug)) {
            throw new Error(`chain "${chain.slug}" is unsupported. Supported chains are: ${this.supportedChains.join(',')}`);
        }
        chain.provider = this.getChainProvider(chain);
        chain.chainId = this.getChainId(chain);
        return chain;
    }
    /**
     * @desc Returns a Token instance.
     * @param {Object} - Token name or model.
     * @returns {Object} - Token model.
     */
    toTokenModel(token) {
        if (typeof token === 'string') {
            const canonicalSymbol = models_1.Token.getCanonicalSymbol(token);
            let { name, decimals } = config_1.metadata.tokens[this.network][canonicalSymbol];
            return new models_1.Token(0, '', decimals, token, name);
        }
        return token;
    }
    /**
     * @desc Calculates current gas price plus increased percentage amount.
     * @param {Object} - Ether's Signer
     * @param {number} - Percentage to bump by.
     * @returns {BigNumber} Bumped as price as BigNumber
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     *const bumpedGasPrice = await hop.getBumpedGasPrice(signer, 1.20)
     *console.log(bumpedGasPrice.toNumber())
     *```
     */
    getBumpedGasPrice(signer, percent) {
        return __awaiter(this, void 0, void 0, function* () {
            const gasPrice = yield signer.getGasPrice();
            return gasPrice.mul(ethers_1.BigNumber.from(percent * 100)).div(ethers_1.BigNumber.from(100));
        });
    }
    /**
     * @desc Returns Chain ID for specified Chain model.
     * @param {Object} - Chain model.
     * @returns {Number} - Chain ID.
     */
    getChainId(chain) {
        const { chainId } = this.chains[this.network][chain.slug];
        return Number(chainId);
    }
    /**
     * @desc Returns the connected signer address.
     * @returns {String} Ethers signer address.
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     *const address = await hop.getSignerAddress()
     *console.log(address)
     *```
     */
    getSignerAddress() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.signer) {
                throw new Error('signer not connected');
            }
            if (ethers_1.Signer.isSigner(this.signer)) {
                return (_a = this.signer) === null || _a === void 0 ? void 0 : _a.getAddress();
            }
            else {
                throw new Error('signer is a provider and has no address');
            }
        });
    }
    /**
     * @desc Returns the connected signer if it's connected to the specified
     * chain id, otherwise it returns a regular provider for the specified chain.
     * @param {Object} chain - Chain name or model
     * @param {Object} signer - Ethers signer or provider
     * @returns {Object} Ethers signer or provider
     */
    getSignerOrProvider(chain, signer = this.signer) {
        return __awaiter(this, void 0, void 0, function* () {
            //console.log('getSignerOrProvider')
            chain = this.toChainModel(chain);
            if (!signer) {
                return chain.provider;
            }
            if (ethers_1.Signer.isSigner(signer)) {
                if (signer.provider) {
                    const connectedChainId = yield signer.getChainId();
                    //console.log('connectedChainId: ', connectedChainId)
                    //console.log('chain.chainId: ', chain.chainId)
                    if (connectedChainId !== chain.chainId) {
                        if (!signer.provider) {
                            //console.log('connect provider')
                            return signer.connect(chain.provider);
                        }
                        //console.log('return chain.provider')
                        return chain.provider;
                    }
                    return signer;
                }
                else {
                    return chain.provider;
                }
            }
            else {
                //console.log('isSigner')
                const { chainId } = yield signer.getNetwork();
                if (chainId !== chain.chainId) {
                    return chain.provider;
                }
                return signer;
            }
        });
    }
    getConfigAddresses(token, chain) {
        var _a, _b;
        token = this.toTokenModel(token);
        chain = this.toChainModel(chain);
        return (_b = (_a = this.addresses[this.network]) === null || _a === void 0 ? void 0 : _a[token.canonicalSymbol]) === null || _b === void 0 ? void 0 : _b[chain.slug];
    }
    getL1BridgeAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l1Bridge;
    }
    getL2BridgeAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l2Bridge;
    }
    getL1CanonicalBridgeAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l1CanonicalBridge;
    }
    getL2CanonicalBridgeAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l2CanonicalBridge;
    }
    getL1CanonicalTokenAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l1CanonicalToken;
    }
    getL2CanonicalTokenAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l2CanonicalToken;
    }
    getL2HopBridgeTokenAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l2HopBridgeToken;
    }
    getL2AmmWrapperAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l2AmmWrapper;
    }
    getL2SaddleSwapAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l2SaddleSwap;
    }
    getL2SaddleLpTokenAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l2SaddleLpToken;
    }
    // Arbitrum ARB Chain address
    getArbChainAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.arbChain;
    }
    // xDai L1 Home AMB bridge address
    getL1AmbBridgeAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l1Amb;
    }
    // xDai L2 AMB bridge address
    getL2AmbBridgeAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l2Amb;
    }
    // Polygon Root Chain Manager address
    getL1PosRootChainManagerAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l1PosRootChainManager;
    }
    // Polygon ERC20 Predicate address
    getL1PosErc20PredicateAddress(token, chain) {
        var _a;
        return (_a = this.getConfigAddresses(token, chain)) === null || _a === void 0 ? void 0 : _a.l1PosPredicate;
    }
    // Transaction overrides options
    txOverrides(chain) {
        return __awaiter(this, void 0, void 0, function* () {
            const txOptions = {};
            if (chain.equals(models_1.Chain.xDai)) {
                txOptions.gasLimit = 5000000;
            }
            if (this.gasPriceMultiplier) {
                txOptions.gasPrice = yield this.getBumpedGasPrice(this.signer, this.gasPriceMultiplier);
            }
            if (chain.equals(models_1.Chain.Optimism)) {
                txOptions.gasPrice = 15000000;
                txOptions.gasLimit = undefined;
            }
            return txOptions;
        });
    }
    getBonderAddress(token) {
        var _a, _b, _c;
        token = this.toTokenModel(token);
        return (_c = (_b = (_a = this.bonders) === null || _a === void 0 ? void 0 : _a[this.network]) === null || _b === void 0 ? void 0 : _b[token.canonicalSymbol]) === null || _c === void 0 ? void 0 : _c[0];
    }
    getBonderAddresses(token) {
        var _a, _b;
        token = this.toTokenModel(token);
        return (_b = (_a = this.bonders) === null || _a === void 0 ? void 0 : _a[this.network]) === null || _b === void 0 ? void 0 : _b[token.canonicalSymbol];
    }
    setGasPriceMultiplier(gasPriceMultiplier) {
        return (this.gasPriceMultiplier = gasPriceMultiplier);
    }
}
exports.default = Base;
